import React from 'react';
import { AxiosError } from 'axios';
import {
  Status as PropertyStatus, OfferStatus, InvoiceType, InvoiceStatus,
} from '../../../domain/types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { Document, Invoice, Property } from '../../../providers/api/dtos';
import useAssignmentByPropertyId from '../use-assignment-by-property-id/useAssignmentByPropertyId';
import useOffersWithStatusExists from '../../../offer/hooks/use-offers-with-status-exists/useOffersWithStatusExists';
import { isAssignmentExpired } from '../../../assignment/utils';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import { canPropertyBeInPreview, canPropertyBeLive } from '../../utils';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { usePropertyDocuments } from '../use-property-documents/usePropertyDocuments';
import useInvoicesByPropertyId from '../../../invoice/hooks/use-invoices-by-property-id/useInvoicesByPropertyId';

export type ComputedPropertyStatus =
  PropertyStatus
  | 'COLLECTED';

export type UseComputedPropertyStatusData = {
  status: ComputedPropertyStatus,
  canBeUpdated: boolean,
  canBeUpdatedTo: (status: ComputedPropertyStatus) => boolean,
};

export type UseComputedPropertyStatusResult = {
  data?: UseComputedPropertyStatusData | undefined,
  isLoading: boolean,
  errors?: Error[] | undefined,
  mutate: () => Promise<void>,
};

const computeCanBeUpdatedToWhenAllMediationsAreCollected = (assignmentCollected: boolean, invoices: Invoice[]): ComputedPropertyStatus[] => {
  const mediationInvoices = invoices.filter(({ type }) => type === InvoiceType.MEDIATION);
  const allMediationInvoicesCollected = mediationInvoices.length > 0
    && mediationInvoices.every(({ status }) => status === InvoiceStatus.COLLECTED);
  return !assignmentCollected && allMediationInvoicesCollected ? ['COLLECTED'] : [];
};

const adminAllowedStatusUpdates = (
  property: Property,
  invoices: Invoice[] = [],
  inProgressOrAcceptedOfferExists: boolean = false,
  assignmentCollected: boolean = false,
  documents: Document[] = [],
): ComputedPropertyStatus[] => {
  const canBeInPreview = canPropertyBeInPreview(property);
  const canBeLive = canPropertyBeLive(property, documents);

  const canBeUpdatedTo: Record<PropertyStatus, ComputedPropertyStatus[]> = {
    [PropertyStatus.DRAFT]: removeNullAndUndefinedValues([
      canBeInPreview ? PropertyStatus.ANTEPRIMA : undefined,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.ANTEPRIMA]: removeNullAndUndefinedValues([
      canBeLive ? PropertyStatus.LIVE : undefined,
      PropertyStatus.DRAFT,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.LIVE]: removeNullAndUndefinedValues([
      inProgressOrAcceptedOfferExists ? PropertyStatus.PROPOSTA : undefined,
      PropertyStatus.DRAFT,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.PROPOSTA]: removeNullAndUndefinedValues([
      canBeLive ? PropertyStatus.LIVE : undefined,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.RITIRATO]: [PropertyStatus.DRAFT],
    [PropertyStatus.VENDUTO]: computeCanBeUpdatedToWhenAllMediationsAreCollected(assignmentCollected, invoices),
  };

  return canBeUpdatedTo[property.status];
};

const contentAllowedStatusUpdates = (
  property: Property,
  inProgressOrAcceptedOfferExists: boolean = false,
  documents: Document[] = [],
): ComputedPropertyStatus[] => {
  const canBeInPreview = canPropertyBeInPreview(property);
  const canBeLive = canPropertyBeLive(property, documents);

  const canBeUpdatedTo: Record<PropertyStatus, ComputedPropertyStatus[]> = {
    [PropertyStatus.DRAFT]: removeNullAndUndefinedValues([
      canBeInPreview ? PropertyStatus.ANTEPRIMA : undefined,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.ANTEPRIMA]: removeNullAndUndefinedValues([
      canBeLive ? PropertyStatus.LIVE : undefined,
      PropertyStatus.DRAFT,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.LIVE]: removeNullAndUndefinedValues([
      inProgressOrAcceptedOfferExists ? PropertyStatus.PROPOSTA : undefined,
      PropertyStatus.DRAFT,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.PROPOSTA]: removeNullAndUndefinedValues([
      canBeLive ? PropertyStatus.LIVE : undefined,
      PropertyStatus.RITIRATO,
    ]),
    [PropertyStatus.RITIRATO]: [PropertyStatus.DRAFT],
    [PropertyStatus.VENDUTO]: [],
  };

  return canBeUpdatedTo[property.status];
};

const financeAllowedStatusUpdates = (
  property: Property,
  invoices: Invoice[] = [],
  assignmentCollected: boolean = false,
) => {
  const canBeUpdatedTo: Record<PropertyStatus, ComputedPropertyStatus[]> = {
    [PropertyStatus.LIVE]: [],
    [PropertyStatus.DRAFT]: [],
    [PropertyStatus.ANTEPRIMA]: [],
    [PropertyStatus.RITIRATO]: [],
    [PropertyStatus.PROPOSTA]: [],
    [PropertyStatus.VENDUTO]: computeCanBeUpdatedToWhenAllMediationsAreCollected(assignmentCollected, invoices),
  };

  return canBeUpdatedTo[property.status];
};

const propertyAgentAndManagerAllowedStatusUpdates = (
  property: Property,
  inProgressOrAcceptedOfferExists: boolean = false,
  assignmentExpired: boolean = false,
  documents: Document[] = [],
): ComputedPropertyStatus[] => {
  const canBeLive = canPropertyBeLive(property, documents);

  const canBeUpdatedTo: Record<PropertyStatus, ComputedPropertyStatus[]> = {
    [PropertyStatus.DRAFT]: [],
    [PropertyStatus.ANTEPRIMA]: removeNullAndUndefinedValues([
      canBeLive ? PropertyStatus.LIVE : undefined,
      PropertyStatus.DRAFT,
    ]),
    [PropertyStatus.LIVE]: removeNullAndUndefinedValues([
      inProgressOrAcceptedOfferExists ? PropertyStatus.PROPOSTA : undefined,
      assignmentExpired ? PropertyStatus.RITIRATO : undefined,
    ]),
    [PropertyStatus.PROPOSTA]: removeNullAndUndefinedValues([
      canBeLive ? PropertyStatus.LIVE : undefined,
    ]),
    [PropertyStatus.RITIRATO]: [PropertyStatus.DRAFT],
    [PropertyStatus.VENDUTO]: [],
  };

  return canBeUpdatedTo[property.status];
};

export const useComputedPropertyStatus = (property: Property): UseComputedPropertyStatusResult => {
  const [errors, setErrors] = React.useState<Error[] | undefined>();

  const handleNewError = React.useCallback((err: AxiosError) => {
    if (err.response?.status === 404) return;

    setErrors((prev) => {
      if (!prev) return [err];
      return [...prev, err];
    });
  }, []);

  const { userIsAdmin, userIsContentEditor, userIsFinance } = useRBAC();

  const {
    data: assignment,
    isLoading: isAssignmentLoading,
    mutate: mutateAssignment,
  } = useAssignmentByPropertyId(property.id, { onError: handleNewError });

  const {
    data: invoices,
    isLoading: areInvoicesLoading,
  } = useInvoicesByPropertyId(property.id, { onError: handleNewError });

  const {
    exist: inProgressOrAcceptedOfferExists,
    isLoading: inProgressOrAcceptedOfferExistsLoading,
    mutate: mutateInProgressOrAcceptedOfferExists,
  } = useOffersWithStatusExists({
    propertyId: property.id,
    offerStatus: [OfferStatus.IN_PROGRESS, OfferStatus.ACCEPTED],
  }, { onError: handleNewError });

  const {
    data: documents,
    isLoading: areDocumentsLoading,
    mutate: mutateDocuments,
  } = usePropertyDocuments(
    property.id,
    { onError: handleNewError },
  );

  const isPropertyAgentOrManager = useCurrentAgentIsSameAgentOrManager(assignment?.agentId);

  const computedPropertyStatus = React.useMemo(() => {
    if (assignment?.isCollected) {
      return 'COLLECTED';
    }

    return property.status;
  }, [assignment?.isCollected, property.status]);

  const canBeUpdatedTo = React.useMemo(() => {
    if (userIsAdmin) {
      return adminAllowedStatusUpdates(
        property,
        invoices?.content,
        inProgressOrAcceptedOfferExists,
        assignment?.isCollected,
        documents,
      );
    }

    if (userIsFinance) {
      return financeAllowedStatusUpdates(
        property,
        invoices?.content,
        assignment?.isCollected,
      );
    }

    if (userIsContentEditor) {
      return contentAllowedStatusUpdates(
        property,
        inProgressOrAcceptedOfferExists,
        documents,
      );
    }

    if (isPropertyAgentOrManager) {
      return propertyAgentAndManagerAllowedStatusUpdates(
        property,
        inProgressOrAcceptedOfferExists,
        assignment ? isAssignmentExpired(assignment) : false,
        documents,
      );
    }

    return [];
  }, [assignment, inProgressOrAcceptedOfferExists, isPropertyAgentOrManager, property, invoices, userIsAdmin, userIsContentEditor, userIsFinance, documents]);

  const canBeUpdatedToFn = React.useCallback(
    (status: ComputedPropertyStatus) => canBeUpdatedTo.includes(status),
    [canBeUpdatedTo],
  );

  const mutate = React.useCallback(async () => {
    await mutateAssignment();
    await mutateInProgressOrAcceptedOfferExists();
    await mutateDocuments();
  }, [mutateAssignment, mutateDocuments, mutateInProgressOrAcceptedOfferExists]);

  const isLoading = React.useMemo(
    () => isAssignmentLoading || inProgressOrAcceptedOfferExistsLoading || areDocumentsLoading || areInvoicesLoading,
    [areDocumentsLoading, areInvoicesLoading, inProgressOrAcceptedOfferExistsLoading, isAssignmentLoading],
  );

  const data: UseComputedPropertyStatusData | undefined = React.useMemo(() => {
    if (errors || isLoading) return undefined;

    return {
      status: computedPropertyStatus,
      canBeUpdated: canBeUpdatedTo.length > 0,
      canBeUpdatedTo: canBeUpdatedToFn,
    };
  }, [canBeUpdatedTo.length, canBeUpdatedToFn, computedPropertyStatus, errors, isLoading]);

  return React.useMemo(() => ({
    data,
    isLoading,
    errors,
    mutate,
  }), [data, errors, isLoading, mutate]);
};
